import { AlertTriangle } from "lucide-react";
import { ToggleButton } from "../../common/ui/toggleSwitch";
import { useGymExpensesConfig } from "../../../hooks/admin/gym/useGymExpensesConfig";
import { getCategoryLabel } from "../../../utils/expense";
import {
  EXPENSE_CATEGORIES,
  type ExpenseCategoryKey,
} from "../../../utils/zod/expensesConfig";

export const ExpensesConfigSection = () => {
  const { config, isLoading, isError, savingCategory, setCategory } =
    useGymExpensesConfig();

  const handleToggle = (
    category: ExpenseCategoryKey,
    isOperational: boolean,
  ) => {
    setCategory({
      category,
      value: isOperational ? "OPERATIONAL" : "NON_OPERATIONAL",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-global-tertiary text-size-xs">
          Cargando configuración…
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="text-size-sm font-semibold text-global-primary">
          Categorías de gastos
        </h4>
        <p className="text-size-2xs text-global-tertiary mt-1">
          Define qué categorías son operacionales (giro corriente: arriendo,
          sueldos, marketing, mantención…) o no operacionales (impuestos,
          créditos, gastos extraordinarios). Afecta los totales en Métricas B2B.
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-global-warning/5 border border-global-warning/20 px-3 py-2">
        <AlertTriangle
          className="w-3.5 h-3.5 text-global-warning shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <p className="text-size-2xs text-global-secondary leading-relaxed">
          <span className="font-semibold text-global-warning">Importante:</span>{" "}
          cambiar una categoría recalcula tanto los meses futuros como los
          reportes históricos.
        </p>
      </div>

      {isError ? (
        <p className="text-size-xs text-global-error text-center py-8">
          No pudimos cargar la configuración de categorías. Recargá la página o
          intentá más tarde.
        </p>
      ) : !config ? (
        <p className="text-size-xs text-global-muted text-center py-8">
          Cargando configuración…
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {EXPENSE_CATEGORIES.map((category) => {
            const value = config.categoryClassification[category];
            const isOperational = value === "OPERATIONAL";
            const isSaving = savingCategory === category;
            return (
              <div
                key={category}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border border-global-primary bg-global-secondary"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-size-xs font-semibold text-global-primary truncate">
                    {getCategoryLabel(category)}
                  </p>
                  <p
                    className={`text-size-2xs mt-0.5 ${
                      isOperational ? "text-global-accent" : "text-global-muted"
                    }`}
                  >
                    {isOperational ? "Operacional" : "No operacional"}
                  </p>
                </div>
                <ToggleButton
                  checked={isOperational}
                  onToggle={(next) => handleToggle(category, next)}
                  disabled={isSaving}
                  aria-label={`Marcar ${getCategoryLabel(category)} como ${
                    isOperational ? "no operacional" : "operacional"
                  }`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
